/**
 * Currency display formatting kept strictly separate from the canonical numeric
 * money values the model and EPS payload carry (WI-TV-W5-002).
 *
 * The wizard stores every money answer as a canonical numeric string (digits and
 * at most one decimal point, no currency symbol, no grouping separators) so
 * `payload-builder.parseMoney` produces the exact same finite number it would
 * have produced from the legacy purchase form. Grouping separators and the
 * dollar sign exist only in what the input displays; they are never persisted
 * into the value store, the form state, or the payload.
 *
 * Lossless invariant: an accepted amount always displays, reparses, and submits
 * as the same numeric meaning. Nothing is silently rounded and nothing is
 * accepted that canonicalizes into text this module would itself reject, so a
 * value is accepted only when its canonical text is exactly the digits supplied.
 * Amounts finer than cents, and magnitudes a double cannot hold exactly (which
 * is also where `String` switches to exponent text, at 1e21), are rejected up
 * front rather than quietly changed.
 */

/** Digits with at most a cents group; no sign, exponent, separators, or sub-cent digits. */
const CANONICAL_MONEY_REGEX = /^\d+(\.\d{1,2})?$/;

/** Guidance for input that is not an exactly representable dollars-and-cents amount. */
export const MONEY_FORMAT_MESSAGE =
  "Enter a dollar amount using digits and at most two decimal places, for example 450000 or 1234.56.";

/** Guidance for manual money input that is not a whole-dollar amount (WI-TV-W65-002). */
export const WHOLE_DOLLAR_FORMAT_MESSAGE =
  "Enter a whole-dollar amount using digits only, for example 450000.";

/** Whole dollars display without a cents group (`450000` -> `$450,000`). */
const WHOLE_DOLLAR_FORMAT = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

/** Cents display, always both digits (`450000.5` -> `$450,000.50`). */
const CENTS_FORMAT = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/** Drops insignificant zeros so supplied digits compare against canonical text. */
function normalizeSuppliedDigits(cleaned: string): string {
  const [whole, fraction = ""] = cleaned.split(".");
  const significantWhole = whole.replace(/^0+(?=\d)/, "");
  const significantFraction = fraction.replace(/0+$/, "");
  return significantFraction === ""
    ? significantWhole
    : `${significantWhole}.${significantFraction}`;
}

/**
 * Parses user money input (which may carry a dollar sign, grouping separators,
 * spaces, or a trailing decimal point) into a finite number. Returns `null` for
 * blank input and for anything that is not an exactly representable
 * dollars-and-cents amount, so callers can treat "absent", "unparseable", and
 * "not representable without changing the amount" as one validation failure
 * instead of silently accepting a different number than the user supplied.
 */
export function parseMoneyInput(raw: string): number | null {
  const cleaned = raw.replace(/[$,\s]/g, "").replace(/\.$/, "");
  if (cleaned === "") return null;
  if (!CANONICAL_MONEY_REGEX.test(cleaned)) return null;
  const parsed = Number(cleaned);
  if (!Number.isFinite(parsed)) return null;
  // Lossless invariant: the number that would be submitted must canonicalize
  // back to exactly the digits supplied. This rejects magnitudes a double
  // rounds (9007199254740993) and magnitudes whose canonical text would be
  // exponent notation (1e21 and above) rather than accepting a changed amount.
  if (canonicalMoneyText(parsed) !== normalizeSuppliedDigits(cleaned)) return null;
  return parsed;
}

/**
 * Canonical numeric text for a money value: exactly what
 * `payload-builder.parseMoney` round-trips back to the same number. Used as the
 * stored value so no display formatting can leak into the payload. Values that
 * {@link parseMoneyInput} accepts always canonicalize to plain decimal text.
 */
export function canonicalMoneyText(value: number): string {
  return String(value);
}

/**
 * Display-only currency rendering (e.g. `450000` -> `$450,000`). Never stored.
 * Returns the input unchanged when it is not parseable so partially typed text
 * is preserved rather than mangled. Because only cents-precision amounts are
 * accepted, the formatted display always denotes the canonical value exactly.
 */
export function formatMoneyForDisplay(raw: string): string {
  const parsed = parseMoneyInput(raw);
  if (parsed === null) return raw;
  return Number.isInteger(parsed)
    ? WHOLE_DOLLAR_FORMAT.format(parsed)
    : CENTS_FORMAT.format(parsed);
}

/** True when a money answer is present, finite, and strictly greater than zero. */
export function isPositiveMoney(raw: string): boolean {
  const parsed = parseMoneyInput(raw);
  return parsed !== null && parsed > 0;
}

/**
 * Canonical text for an accepted money answer, or `null` when the answer is not
 * an amount {@link parseMoneyInput} accepts. This is the text form every derived
 * amount is built from and serialized back to.
 */
export function canonicalMoneyTextFromInput(raw: string): string | null {
  const parsed = parseMoneyInput(raw);
  return parsed === null ? null : canonicalMoneyText(parsed);
}

/**
 * Integer count of cents for canonical money text, as a `BigInt`.
 *
 * The conversion is purely lexical — split the decimal text and right-pad the
 * fractional part to two digits — so no binary floating-point step can round the
 * amount, and no magnitude the money contract accepts is truncated at
 * `Number.MAX_SAFE_INTEGER`. Returns `null` for text outside the canonical
 * dollars-and-cents grammar.
 */
export function moneyTextToCents(canonical: string): bigint | null {
  if (!CANONICAL_MONEY_REGEX.test(canonical)) return null;
  const [whole, fraction = ""] = canonical.split(".");
  return BigInt(`${whole}${fraction.padEnd(2, "0")}`);
}

/**
 * Serializes an integer count of cents back to the helper's canonical plain
 * decimal form: no fraction for whole dollars, no insignificant trailing
 * fractional zero, and never exponent notation. Returns `null` for a negative
 * amount, which is not a money value.
 */
export function centsToMoneyText(cents: bigint): string | null {
  if (cents < 0n) return null;
  const digits = cents.toString().padStart(3, "0");
  const whole = digits.slice(0, -2).replace(/^0+(?=\d)/, "");
  const fraction = digits.slice(-2).replace(/0+$/, "");
  return fraction === "" ? whole : `${whole}.${fraction}`;
}

/**
 * Subtracts one accepted money answer from another entirely in integer cents and
 * returns the canonical text of a strictly positive difference.
 *
 * Both operands must first satisfy the lossless lexical money contract, the
 * subtraction happens on `BigInt` cents rather than on `number` dollars or
 * `number` cents, and the serialized difference must itself be an amount
 * {@link parseMoneyInput} accepts before it can reach the payload boundary.
 * Returns `null` when either operand is not accepted, when the difference is
 * zero or negative, or when the difference is not itself a representable amount.
 */
export function subtractMoneyText(minuendRaw: string, subtrahendRaw: string): string | null {
  const minuend = canonicalMoneyTextFromInput(minuendRaw);
  const subtrahend = canonicalMoneyTextFromInput(subtrahendRaw);
  if (minuend === null || subtrahend === null) return null;

  const minuendCents = moneyTextToCents(minuend);
  const subtrahendCents = moneyTextToCents(subtrahend);
  if (minuendCents === null || subtrahendCents === null) return null;

  const cents = minuendCents - subtrahendCents;
  if (cents <= 0n) return null;

  const text = centsToMoneyText(cents);
  if (text === null) return null;
  // The derived amount is only usable when the authoritative helper accepts it
  // exactly, so a difference that could not be submitted losslessly is refused
  // rather than rounded into a different amount.
  return canonicalMoneyTextFromInput(text) === text ? text : null;
}

/**
 * WI-TV-W65-002 shared whole-dollar dynamic-money primitive.
 *
 * Manual entry into a purchase money `<input>` is whole dollars only: the field
 * shows grouped currency text (`$###,###,###`) as the borrower types, pastes,
 * deletes, or edits — never a decimal point or cents. Decimal, unsupported, or
 * beyond-maximum input is rejected at the input edge and leaves the prior
 * accepted display and canonical value untouched. Display formatting stays
 * lexical (digit grouping and the dollar sign only); it never runs through
 * `Number`, `parseFloat`, or `toFixed`, so it cannot round or widen a value.
 *
 * A trusted decimal prefill the existing sanitizer accepted is kept exactly in
 * the value store and in every comparison and derivation, while its display is
 * the whole-dollar portion truncated toward zero (`1234.99` -> `$1,234`); the
 * first accepted manual whole-dollar edit replaces that exact decimal string.
 */

/** True for a single ASCII digit `0`–`9`. */
function isDigit(ch: string): boolean {
  return ch >= "0" && ch <= "9";
}

/** Groups a run of whole-dollar digits in threes (`1234567` -> `1,234,567`). */
function groupWholeDollars(digits: string): string {
  return digits.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

/** A whole-dollar manual entry: one or more digits; no sign, point, or exponent. */
const WHOLE_DOLLAR_INPUT_REGEX = /^\d+$/;

/**
 * A well-formed whole-dollar display candidate: an optional single leading
 * dollar sign followed only by digits and comma group separators. This is the
 * exact grammar the field ever renders, so it is validated on the raw candidate
 * *before* any display formatting is stripped. Anything else — whitespace of
 * any kind, an embedded or repeated dollar sign, letters, a decimal point — is
 * not allowed display formatting and is rejected without conversion. An empty
 * string is handled separately (it is a deletion, not an invalid candidate).
 */
const WHOLE_DOLLAR_CANDIDATE_REGEX = /^\$?[\d,]*$/;

/** The outcome of classifying a raw manual/candidate money edit. */
type WholeDollarCandidate =
  | { kind: "empty" }
  | { kind: "invalid" }
  | { kind: "value"; canonical: string };

/**
 * Classifies a raw candidate against the whole-dollar display grammar without
 * ever rewriting it into a different amount. The raw grammar is validated first
 * so unsupported whitespace and embedded/repeated dollar signs are rejected
 * outright rather than being silently stripped into a valid-looking amount;
 * only then are the allowed `$`/`,` display characters removed and the digits
 * routed through {@link parseMoneyInput} to preserve the exact wire-safe
 * maximum and exact-representation rules (beyond-maximum is rejected, not
 * converted). A field that is empty, or contains only the allowed formatting
 * characters with no digits, is a deletion.
 */
function classifyWholeDollarCandidate(raw: string): WholeDollarCandidate {
  if (raw === "") return { kind: "empty" };
  if (!WHOLE_DOLLAR_CANDIDATE_REGEX.test(raw)) return { kind: "invalid" };
  const digits = raw.replace(/[$,]/g, "");
  if (digits === "") return { kind: "empty" };
  if (!WHOLE_DOLLAR_INPUT_REGEX.test(digits)) return { kind: "invalid" };
  const parsed = parseMoneyInput(digits);
  return parsed === null ? { kind: "invalid" } : { kind: "value", canonical: canonicalMoneyText(parsed) };
}

/**
 * Formats whole-dollar money text mid-edit, adding only the dollar sign and
 * grouping separators and dropping insignificant leading zeros. An empty (or
 * separator-only) field stays blank. A candidate that is not a whole-dollar
 * entry — whitespace, an embedded/repeated dollar sign, a decimal point, sign,
 * exponent, letters, or anything else — is returned exactly as supplied so the
 * binding rejects it at the edge instead of rewriting it into a different
 * amount. Purely lexical: no numeric parse of the display occurs.
 */
export function formatWholeDollarWhileTyping(raw: string): string {
  const candidate = classifyWholeDollarCandidate(raw);
  if (candidate.kind === "empty") return "";
  if (candidate.kind === "invalid") return raw;
  return `$${groupWholeDollars(candidate.canonical)}`;
}

/**
 * Canonical whole-dollar text for accepted manual input, or `null` when the
 * input is not a whole-dollar amount the lossless money contract accepts
 * (including unsupported whitespace or embedded/repeated dollar signs). The
 * value is routed through {@link parseMoneyInput} so the existing wire-safe
 * maximum and exact-representation rules are preserved and beyond-maximum entry
 * is rejected rather than converted.
 */
export function wholeDollarCanonicalFromInput(raw: string): string | null {
  const candidate = classifyWholeDollarCandidate(raw);
  return candidate.kind === "value" ? candidate.canonical : null;
}

/**
 * Whole-dollar display for an accepted canonical money value, truncating any
 * fractional part of a trusted decimal prefill toward zero with exact integer
 * (`BigInt`) logic — `1234.99` renders as `$1,234`, never `$1,235`. Returns the
 * input unchanged when it is not canonical money (e.g. blank), so partially
 * mounted state is never mangled.
 */
export function formatWholeDollarForDisplay(canonical: string): string {
  const cents = moneyTextToCents(canonical);
  if (cents === null) return canonical;
  return `$${groupWholeDollars((cents / 100n).toString())}`;
}

/** Count of canonical digits (`0`–`9`) strictly to the left of `index`. */
export function digitsToLeft(text: string, index: number): number {
  const bound = Math.max(0, Math.min(index, text.length));
  let count = 0;
  for (let i = 0; i < bound; i += 1) if (isDigit(text[i])) count += 1;
  return count;
}

/**
 * The caret offset in `text` immediately after its `digitCount`-th canonical
 * digit. A zero (or negative) count maps to the position just before the first
 * digit — after any synthesized display prefix such as the dollar sign — so the
 * caret is never stranded in front of the `$` when the borrower is editing at
 * the start of the amount; text with no digits at all maps to the string start.
 * A count past the last digit maps to the string end. This is the inverse of
 * {@link digitsToLeft} and is what keeps the caret anchored to the same digit
 * boundary across reformatting.
 */
export function offsetForDigitCount(text: string, digitCount: number): number {
  if (digitCount <= 0) {
    for (let i = 0; i < text.length; i += 1) if (isDigit(text[i])) return i;
    return 0;
  }
  let count = 0;
  for (let i = 0; i < text.length; i += 1) {
    if (isDigit(text[i])) {
      count += 1;
      if (count === digitCount) return i + 1;
    }
  }
  return text.length;
}

/**
 * Binds whole-dollar dynamic currency formatting to a purchase money input.
 *
 * Manual entry is whole dollars only. On every accepted edit the display is
 * regrouped with {@link formatWholeDollarWhileTyping} and the caret is restored
 * by preserving the count of digits to the left of each boundary, so grouping
 * separators and the dollar sign inserted ahead of the caret never push it off
 * the digit being edited. A decimal point or any other unsupported keystroke,
 * paste, or committed composition is rejected at the edge: the prior accepted
 * display, caret, and canonical value are restored unchanged, and an empty field
 * with no prior value stays empty. Formatting is suppressed while an IME
 * composition is active and runs once on `compositionend`. Blur re-renders the
 * whole-dollar display of the exact stored value — which may be an untouched
 * decimal prefill — without changing it.
 *
 * `initialCanonical` seeds the exact stored value so a trusted decimal prefill
 * stays exact (displayed truncated) until the borrower's first accepted manual
 * edit replaces it. The canonical value (or `null` when the field is empty) is
 * handed to `onCanonicalChange` on each accepted edit so the caller owns the
 * value store and any derived amount.
 */
export function bindWholeDollarMoneyInput(
  input: HTMLInputElement,
  onCanonicalChange: (canonical: string | null, raw: string) => void,
  initialCanonical: string | null = null,
): void {
  let composing = false;
  // The exact canonical value, which may be a sanitizer-accepted decimal
  // prefill; it stays exact until the first accepted manual whole-dollar edit.
  let canonical: string | null =
    initialCanonical === null || initialCanonical === "" ? null : initialCanonical;
  // The last accepted display, restored verbatim when an edit is rejected so a
  // decimal or unsupported keystroke changes nothing the borrower can see.
  let acceptedDisplay = canonical === null ? "" : formatWholeDollarForDisplay(canonical);
  input.value = acceptedDisplay;

  // The complete pre-composition state captured at `compositionstart`, restored
  // verbatim when a composition commits an invalid (including empty) final.
  interface CompositionSnapshot {
    canonical: string | null;
    display: string;
    start: number;
    end: number;
    direction: "forward" | "backward" | "none";
  }
  let snapshot: CompositionSnapshot | null = null;
  // After a composition resolves, the browser fires one trailing `input` event
  // carrying the finalized value. This holds that exact resolved DOM value so
  // the single matching trailing input can be deduplicated without swallowing a
  // genuine later edit; it is cleared on the next input, a new composition, or
  // blur.
  let pendingFinalInputValue: string | null = null;
  // The exact selection captured at `beforeinput` for a non-composition edit,
  // i.e. before the browser mutates the DOM. When that edit is rejected it is
  // restored verbatim (range and direction) so a rejected keystroke or paste
  // over an active selection leaves the borrower's selection exactly as it was
  // rather than collapsing the caret.
  let preEditSelection:
    | { start: number; end: number; direction: "forward" | "backward" | "none" }
    | null = null;

  const setSelection = (
    start: number,
    end: number,
    direction: "forward" | "backward" | "none" = "none",
  ): void => {
    try {
      input.setSelectionRange(start, end, direction);
    } catch {
      // Some environments disallow selection on the input type; the reformat
      // still applied, only caret restoration is skipped.
    }
  };

  const applyEdit = (): void => {
    const raw = input.value;
    // Consume the pre-edit selection captured at `beforeinput` (if any) exactly
    // once for this edit so a later programmatic input cannot reuse a stale one.
    const pre = preEditSelection;
    preEditSelection = null;
    const candidate = classifyWholeDollarCandidate(raw);

    if (candidate.kind === "empty") {
      // Deleting all content clears the canonical and any derived state.
      canonical = null;
      acceptedDisplay = "";
      if (raw !== "") input.value = "";
      setSelection(0, 0);
      onCanonicalChange(null, "");
      return;
    }

    if (candidate.kind === "invalid") {
      // Decimal/unsupported/beyond-maximum input — including whitespace and
      // embedded/repeated dollar signs — is rejected at the edge: the prior
      // accepted display, canonical value, and selection are left unchanged.
      // Capture the post-mutation caret before resetting the value (which would
      // otherwise clamp the selection to the restored, shorter display).
      const rejectedCaret = input.selectionStart ?? acceptedDisplay.length;
      input.value = acceptedDisplay;
      if (pre !== null) {
        // Restore the exact pre-edit selection (range and direction) captured at
        // `beforeinput`, so replacing an active selection with an invalid
        // candidate does not collapse it.
        const start = Math.max(0, Math.min(pre.start, acceptedDisplay.length));
        const end = Math.max(0, Math.min(pre.end, acceptedDisplay.length));
        setSelection(start, end, pre.direction);
      } else {
        // Fallback when no `beforeinput` was observed: best-effort collapsed
        // caret adjusted for the rejected insertion length.
        const removed = raw.length - acceptedDisplay.length;
        const caret = rejectedCaret - Math.max(0, removed);
        const clamped = Math.max(0, Math.min(caret, acceptedDisplay.length));
        setSelection(clamped, clamped);
      }
      return;
    }

    const startDigits = digitsToLeft(raw, input.selectionStart ?? raw.length);
    const endDigits = digitsToLeft(raw, input.selectionEnd ?? raw.length);
    const formatted = formatWholeDollarWhileTyping(raw);
    canonical = candidate.canonical;
    acceptedDisplay = formatted;
    if (formatted !== raw) input.value = formatted;
    setSelection(
      offsetForDigitCount(formatted, startDigits),
      offsetForDigitCount(formatted, endDigits),
    );
    onCanonicalChange(canonical, formatted);
  };

  const resolveComposition = (base: CompositionSnapshot): void => {
    // Resolve the composed final exactly once against the pre-composition
    // snapshot. In composition context every non-whole-dollar final — including
    // the empty string and unsupported whitespace/dollar-sign candidates — is
    // invalid and rolls the field fully back.
    const raw = input.value;
    const candidate = classifyWholeDollarCandidate(raw);

    if (candidate.kind !== "value") {
      // Invalid/decimal/unsupported/beyond-max/empty final: restore the exact
      // display, canonical, and selection range/direction; publish nothing so
      // external model/derived state is untouched.
      canonical = base.canonical;
      acceptedDisplay = base.display;
      input.value = base.display;
      setSelection(base.start, base.end, base.direction);
      return;
    }

    const startDigits = digitsToLeft(raw, input.selectionStart ?? raw.length);
    const endDigits = digitsToLeft(raw, input.selectionEnd ?? raw.length);
    const formatted = formatWholeDollarWhileTyping(raw);
    canonical = candidate.canonical;
    acceptedDisplay = formatted;
    input.value = formatted;
    setSelection(
      offsetForDigitCount(formatted, startDigits),
      offsetForDigitCount(formatted, endDigits),
    );
    onCanonicalChange(canonical, formatted);
  };

  input.addEventListener("beforeinput", () => {
    // Snapshot the exact selection before the browser mutates the DOM for a
    // non-composition edit (keyboard or paste). Composition edits use their own
    // `compositionstart` snapshot, so skip while composing.
    if (composing) return;
    preEditSelection = {
      start: input.selectionStart ?? acceptedDisplay.length,
      end: input.selectionEnd ?? acceptedDisplay.length,
      direction: input.selectionDirection ?? "none",
    };
  });
  input.addEventListener("compositionstart", () => {
    // Begin an atomic transaction: capture the complete prior accepted state
    // (display, canonical, and exact selection) so an invalid final can be
    // rolled back verbatim. In-progress input events publish nothing.
    composing = true;
    preEditSelection = null;
    pendingFinalInputValue = null;
    snapshot = {
      canonical,
      display: acceptedDisplay,
      start: input.selectionStart ?? acceptedDisplay.length,
      end: input.selectionEnd ?? acceptedDisplay.length,
      direction: input.selectionDirection ?? "none",
    };
  });
  input.addEventListener("compositionend", () => {
    // Resolve the composed result exactly once against the snapshot, then mark
    // the resolved DOM value so the browser's single trailing `input` event for
    // this composition is deduplicated rather than re-applied.
    composing = false;
    const base = snapshot ?? {
      canonical,
      display: acceptedDisplay,
      start: input.selectionStart ?? acceptedDisplay.length,
      end: input.selectionEnd ?? acceptedDisplay.length,
      direction: input.selectionDirection ?? "none",
    };
    snapshot = null;
    resolveComposition(base);
    pendingFinalInputValue = input.value;
  });
  input.addEventListener("input", () => {
    // While composing, the raw in-progress text is left in the DOM but never
    // mutates the accepted canonical state and never publishes a change;
    // nothing is committed until `compositionend`.
    if (composing) return;
    if (pendingFinalInputValue !== null) {
      // Consume exactly the browser's trailing final input for the just-resolved
      // composition. Clearing on this next input is deterministic: a matching
      // value is deduplicated, and any different (genuine) edit still applies.
      const resolved = pendingFinalInputValue;
      pendingFinalInputValue = null;
      if (input.value === resolved) return;
    }
    applyEdit();
  });
  input.addEventListener("blur", () => {
    // Re-render the truncated whole-dollar display of the exact stored value
    // (which may be an untouched decimal prefill) without changing it.
    pendingFinalInputValue = null;
    snapshot = null;
    acceptedDisplay = canonical === null ? "" : formatWholeDollarForDisplay(canonical);
    input.value = acceptedDisplay;
    onCanonicalChange(canonical, acceptedDisplay);
  });
}
